/**
 * Google Analytics integration that respects the user's GDPR cookie choice.
 * - We default to "denied" via Consent Mode v2 BEFORE gtag.js loads, so any
 *   pageview that fires before the user clicks accept/reject still complies.
 * - If the user accepts, we update consent to granted and load the gtag script.
 * - If the user rejects, gtag is never loaded, no cookies are dropped.
 */

const GA_MEASUREMENT_ID = 'G-B68Y2YGT11';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let scriptLoaded = false;

function bootstrapDataLayer() {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer!.push(args);
    };
  }
}

/** Always set defaults to "denied" up-front so any later events are compliant. */
export function initConsentDefaults() {
  if (typeof window === 'undefined') return;
  bootstrapDataLayer();
  window.gtag!('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'granted', // strictly necessary
    security_storage: 'granted',
    wait_for_update: 500,
  });
}

function loadGtagScript() {
  if (scriptLoaded) return;
  scriptLoaded = true;
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(s);

  bootstrapDataLayer();
  window.gtag!('js', new Date());
  window.gtag!('config', GA_MEASUREMENT_ID, {
    anonymize_ip: true,
    send_page_view: true,
  });
}

export function grantAnalyticsConsent() {
  if (typeof window === 'undefined') return;
  bootstrapDataLayer();
  window.gtag!('consent', 'update', {
    analytics_storage: 'granted',
  });
  loadGtagScript();
}

export function revokeAnalyticsConsent() {
  if (typeof window === 'undefined') return;
  bootstrapDataLayer();
  window.gtag!('consent', 'update', {
    analytics_storage: 'denied',
  });
}

/**
 * Manually fire a pageview (useful when react-router navigates client-side and
 * we want each route change tracked).
 */
export function trackPageview(path: string) {
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
  });
}
